import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { getOrderTimeBatch } from 'requests/order'
import { selectToken } from 'store/users/users.selectors'
import { OrderTimeBatchItem } from 'types/order-time'

interface OrderTimeData {
  currentTime: number
  clarificationTime: number
}

interface UseOrderTimesResult {
  times: { [orderId: string]: OrderTimeData }
  loading: boolean
  error: Error | null
}

export const useOrderTimes = (orderIds: string[], refreshInterval: number = 60000): UseOrderTimesResult => {
  const token = useSelector(selectToken) as string
  const [times, setTimes] = useState<{ [orderId: string]: OrderTimeData }>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Создаем строку из массива для сравнения в зависимостях
  const orderIdsKey = useMemo(() => (orderIds || []).join(','), [orderIds])

  const fetchTimes = useCallback(() => {
    if (!orderIds || orderIds.length === 0 || !token) {
      setTimes({})
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const currentOrderIds = [...orderIds] // Копируем массив для использования в промисах
    getOrderTimeBatch(currentOrderIds, token)
      .then((data: OrderTimeBatchItem[]) => {
        const timesMap: { [orderId: string]: OrderTimeData } = data
          ? data.reduce((acc, item) => {
              acc[item.orderId] = {
                currentTime: item.currentTime,
                clarificationTime: item.clarificationTime,
              }
              return acc
            }, {} as { [orderId: string]: OrderTimeData })
          : {}
        setTimes(timesMap)
        setLoading(false)
      })
      .catch((err) => {
        setError(err instanceof Error ? err : new Error(String(err)))
        setLoading(false)
        console.error('[useOrderTimes] Error fetching order times:', err)
      })
  }, [orderIds, token])

  useEffect(() => {
    fetchTimes()

    const intervalId = setInterval(() => {
      fetchTimes()
    }, refreshInterval)

    return () => {
      clearInterval(intervalId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderIdsKey, token, refreshInterval])

  return useMemo(() => ({ times, loading, error }), [times, loading, error])
}
